/**
 * GrantProfile — `/grant-profile`.
 *
 * "Synced from your business profile" hint chips decay as the user
 * edits (backend emits `prefilled_from_user_profile` and drops any
 * field name from that list on PATCH). We NEVER write back to
 * `user.profile` — this is a Grant Navigator-only surface.
 */
import { useEffect, useState } from "react";
import { Save, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import GrantShell from "@/components/grants/GrantShell";
import { PageHeader } from "@/design-system/components/PageHeader";
import { Card } from "@/design-system/components/Card";
import { toast } from "sonner";

const KNOWN_CERTS = [
  { key: "wosb", label: "WOSB — Woman-Owned Small Business" },
  { key: "edwosb", label: "EDWOSB — Economically Disadvantaged WOSB" },
  { key: "mbe", label: "MBE — Minority Business Enterprise" },
  { key: "vosb", label: "VOSB — Veteran-Owned Small Business" },
  { key: "sdvosb", label: "SDVOSB — Service-Disabled VOSB" },
  { key: "8a", label: "SBA 8(a) Business Development Program" },
  { key: "hubzone", label: "SBA HUBZone" },
  { key: "disadvantaged_business_enterprise", label: "Disadvantaged Business Enterprise" },
];

const KNOWN_DOCS = [
  "tax_returns",
  "balance_sheet",
  "income_statement",
  "profit_loss",
  "bank_statements",
  "business_plan",
  "capability_statement",
  "insurance_docs",
  "resumes",
  "articles_of_organization",
  "budget_narrative",
  "letters_of_support",
];

function SyncedHint({ visible }) {
  if (!visible) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold"
      style={{ background: "var(--ds-color-info-soft)", color: "var(--ds-color-info)", border: "1px solid var(--ds-color-info-soft)" }}
    >
      <CheckCircle2 className="w-3 h-3" /> Synced from your business profile
    </span>
  );
}

function Field({ label, name, prefilledSet, children }) {
  return (
    <label className="block">
      <span className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
        {label} <SyncedHint visible={prefilledSet.has(name)} />
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function TextInput({ testId, name, value, onChange, placeholder = "", type = "text" }) {
  return (
    <input
      type={type}
      data-testid={testId}
      value={value ?? ""}
      onChange={(e) => onChange(name, e.target.value === "" ? null : (type === "number" ? Number(e.target.value) : e.target.value))}
      placeholder={placeholder}
      className="block w-full rounded-[var(--ds-radius-field)] px-3 py-2 text-[13px] focus:outline-none"
      style={{ background: "var(--ds-color-surface)", border: "1px solid var(--ds-color-border)", color: "var(--ds-color-ink)" }}
    />
  );
}

/**
 * Textarea — multiline variant used for the Phase G2A mission_statement
 * field. Soft ~500-char guidance is shown in the label (not enforced),
 * because the backend model accepts any string length.
 */
function Textarea({ testId, name, value, onChange, placeholder = "", rows = 3 }) {
  return (
    <textarea
      data-testid={testId}
      value={value ?? ""}
      onChange={(e) => onChange(name, e.target.value === "" ? null : e.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={800}
      className="block w-full rounded-[var(--ds-radius-field)] px-3 py-2 text-[13px] focus:outline-none leading-snug"
      style={{ background: "var(--ds-color-surface)", border: "1px solid var(--ds-color-border)", color: "var(--ds-color-ink)" }}
    />
  );
}

function BoolTri({ testId, name, value, onChange }) {
  // null / true / false, tri-state so we can differentiate "unset" from "no".
  return (
    <div className="flex gap-2">
      {[{ v: true, label: "Yes" }, { v: false, label: "No" }, { v: null, label: "Unset" }].map((opt) => {
        const active = value === opt.v;
        return (
          <button
            type="button"
            key={String(opt.v)}
            data-testid={`${testId}-${opt.v === null ? "unset" : opt.v ? "yes" : "no"}`}
            onClick={() => onChange(name, opt.v)}
            className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-300"
            style={{
              background: active ? "var(--ds-color-accent)" : "transparent",
              color: active ? "var(--ds-color-background)" : "var(--ds-color-ink)",
              border: `1px solid ${active ? "var(--ds-color-accent)" : "var(--ds-color-border)"}`,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function MultiSelect({ testId, name, options, values, onChange }) {
  const set = new Set(values || []);
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const label = typeof o === "string" ? o.replace(/_/g, " ") : o.label;
        const key = typeof o === "string" ? o : o.key;
        const active = set.has(key);
        return (
          <button
            key={key}
            type="button"
            data-testid={`${testId}-${key}`}
            onClick={() => {
              const next = new Set(set);
              if (active) next.delete(key); else next.add(key);
              onChange(name, [...next]);
            }}
            className="px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300"
            style={{
              background: active ? "var(--ds-color-accent-soft)" : "transparent",
              color: active ? "var(--ds-color-accent)" : "var(--ds-color-ink-muted)",
              border: `1px solid ${active ? "var(--ds-color-accent-soft)" : "var(--ds-color-border)"}`,
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default function GrantProfile() {
  const [state, setState] = useState({ loading: true, profile: null, saving: false, savedAt: null });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await api.get("/grant-profile");
        if (!cancelled) setState({ loading: false, profile: res.data, saving: false, savedAt: null });
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  function update(name, value) {
    setState((s) => ({ ...s, profile: { ...s.profile, [name]: value } }));
  }

  async function save() {
    if (!state.profile) return;
    setState((s) => ({ ...s, saving: true }));
    try {
      const {
        id, user_id, updated_at, prefilled_from_user_profile,  // strip read-only fields
        ...body
      } = state.profile;
      const res = await api.patch("/grant-profile", body);
      setState({ loading: false, profile: res.data, saving: false, savedAt: new Date() });
      toast.success("Grant profile updated.");
    } catch (err) {
      setState((s) => ({ ...s, saving: false }));
      toast.error(formatApiError(err));
    }
  }

  if (state.loading || !state.profile) {
    return (
      <GrantShell testId="grant-profile-root">
        <div className="mt-10 text-sm text-[var(--ds-color-ink-muted)]">Loading grant profile…</div>
      </GrantShell>
    );
  }

  const p = state.profile;
  const prefilledSet = new Set(p.prefilled_from_user_profile || []);

  return (
    <GrantShell testId="grant-profile-root">
      <PageHeader
        eyebrow="Grant Navigator"
        title="Your Grant Profile"
        description="Complete these fields so every eligibility snapshot renders on more than 'potentially eligible' hunches. Fields marked with the sync badge were pre-filled from your business profile — editing here never overwrites that record."
        data-testid="grant-profile-header"
      />

      <Card className="mt-2 rounded-[var(--ds-radius-card)]">
        <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Business age (months)" name="business_age_months" prefilledSet={prefilledSet}>
            <TextInput testId="gp-business-age-months" name="business_age_months" value={p.business_age_months} onChange={update} type="number" />
          </Field>
          <Field label="Annual revenue" name="annual_revenue" prefilledSet={prefilledSet}>
            <TextInput testId="gp-annual-revenue" name="annual_revenue" value={p.annual_revenue} onChange={update} type="number" placeholder="e.g., 180000" />
          </Field>
          <Field label="Industry" name="industry" prefilledSet={prefilledSet}>
            <TextInput testId="gp-industry" name="industry" value={p.industry} onChange={update} placeholder="e.g., healthcare, agriculture" />
          </Field>
          <Field label="Entity type" name="entity_type" prefilledSet={prefilledSet}>
            <select
              data-testid="gp-entity-type"
              value={p.entity_type ?? ""}
              onChange={(e) => update("entity_type", e.target.value || null)}
              className="block w-full rounded-[var(--ds-radius-field)] px-3 py-2 text-[13px] focus:outline-none"
              style={{ background: "var(--ds-color-surface)", border: "1px solid var(--ds-color-border)", color: "var(--ds-color-ink)" }}
            >
              <option value="">(unset)</option>
              <option value="nonprofit">Nonprofit</option>
              <option value="for_profit">For-profit</option>
              <option value="sole_prop">Sole proprietorship</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Employee count" name="employee_count" prefilledSet={prefilledSet}>
            <TextInput testId="gp-employee-count" name="employee_count" value={p.employee_count} onChange={update} type="number" />
          </Field>
          <Field label="State" name="state" prefilledSet={prefilledSet}>
            <TextInput testId="gp-state" name="state" value={p.state} onChange={update} placeholder="e.g., CA" />
          </Field>
          <Field label="ZIP" name="zip" prefilledSet={prefilledSet}>
            <TextInput testId="gp-zip" name="zip" value={p.zip} onChange={update} placeholder="e.g., 94103" />
          </Field>
        </div>

        {/* Phase G2A CP3 — mission_statement powers the AI narrative match
            layer (see prompts/match_narrative_v1.py). Optional but
            strongly recommended. Never written back to user.profile. */}
        <div className="mt-1">
          <label className="block text-xs uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
            Mission statement
          </label>
          <p className="mt-1 text-[11px] text-[var(--ds-color-ink-muted)] leading-relaxed">
            One or two sentences on your business's mission and community
            impact. Used to compute AI narrative match scores. ~500-character
            soft limit; leave blank to skip narrative analysis.
          </p>
          <div className="mt-2">
            <Textarea
              testId="gp-mission-statement"
              name="mission_statement"
              value={p.mission_statement}
              onChange={update}
              placeholder="e.g., We help underserved small businesses in the Bay Area access working capital and community lending programs, prioritising women- and minority-owned firms in low-income tracts."
              rows={3}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="EIN registered" name="ein_registered" prefilledSet={prefilledSet}>
            <BoolTri testId="gp-ein-registered" name="ein_registered" value={p.ein_registered} onChange={update} />
          </Field>
          <Field label="SAM.gov registered" name="sam_gov_registered" prefilledSet={prefilledSet}>
            <BoolTri testId="gp-sam-gov" name="sam_gov_registered" value={p.sam_gov_registered} onChange={update} />
          </Field>
          <Field label="UEI on file" name="uei_on_file" prefilledSet={prefilledSet}>
            <BoolTri testId="gp-uei" name="uei_on_file" value={p.uei_on_file} onChange={update} />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Woman-owned" name="is_woman_owned" prefilledSet={prefilledSet}>
            <BoolTri testId="gp-woman-owned" name="is_woman_owned" value={p.is_woman_owned} onChange={update} />
          </Field>
          <Field label="Minority-owned" name="is_minority_owned" prefilledSet={prefilledSet}>
            <BoolTri testId="gp-minority-owned" name="is_minority_owned" value={p.is_minority_owned} onChange={update} />
          </Field>
          <Field label="Veteran-owned" name="is_veteran_owned" prefilledSet={prefilledSet}>
            <BoolTri testId="gp-veteran-owned" name="is_veteran_owned" value={p.is_veteran_owned} onChange={update} />
          </Field>
          <Field label="Rural" name="is_rural" prefilledSet={prefilledSet}>
            <BoolTri testId="gp-rural" name="is_rural" value={p.is_rural} onChange={update} />
          </Field>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">Certifications</p>
          <div className="mt-2">
            <MultiSelect testId="gp-cert" name="certifications" options={KNOWN_CERTS} values={p.certifications} onChange={update} />
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">Documents available</p>
          <div className="mt-2">
            <MultiSelect testId="gp-doc" name="financial_documents_available" options={KNOWN_DOCS} values={p.financial_documents_available} onChange={update} />
          </div>
        </div>

        <Field label="Business plan status" name="business_plan_status" prefilledSet={prefilledSet}>
          <select
            data-testid="gp-business-plan-status"
            value={p.business_plan_status ?? ""}
            onChange={(e) => update("business_plan_status", e.target.value || null)}
            className="block w-full rounded-[var(--ds-radius-field)] px-3 py-2 text-[13px] focus:outline-none"
            style={{ background: "var(--ds-color-surface)", border: "1px solid var(--ds-color-border)", color: "var(--ds-color-ink)" }}
          >
            <option value="">(unset)</option>
            <option value="none">Not started</option>
            <option value="draft">Draft in progress</option>
            <option value="complete">Complete</option>
          </select>
        </Field>
        </div>
      </Card>

      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={state.saving}
          data-testid="grant-profile-save-btn"
          className="inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold transition-colors disabled:opacity-60"
          style={{
            background: "var(--ds-color-accent)",
            color: "var(--ds-color-accent-ink)",
            transitionDuration: "var(--ds-duration-fast)",
          }}
          onMouseEnter={(e) =>
            !state.saving &&
            (e.currentTarget.style.background = "var(--ds-color-accent-strong)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "var(--ds-color-accent)")
          }
        >
          <Save className="w-4 h-4" /> {state.saving ? "Saving…" : "Save changes"}
        </button>
        {state.savedAt && (
          <span
            className="inline-flex items-center gap-1.5 text-[11px]"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            <CheckCircle2
              className="w-3.5 h-3.5"
              style={{ color: "var(--ds-color-success)" }}
            />{" "}
            Saved just now
          </span>
        )}
      </div>
    </GrantShell>
  );
}
